import React, { useEffect } from 'react';

const Downloads = () => {
  useEffect(() => {
    document.title = 'Downloads - KPCA Documents & Resources';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Download important KPCA documents including Constitution, Bylaws, and Current Procurement Act. Access essential resources for professional cleaners in Kenya.');
    }
  }, []);

  const documents = [
    {
      id: 1,
      title: 'KPCA Constitution',
      description: 'The official constitution governing the Kenya Professional Cleaners Association',
      fileSize: '2.1 MB',
      lastUpdated: 'March 2024',
      category: 'Governance',
      downloadUrl: '/documents/kpca-constitution.pdf',
      icon: '📋'
    },
    {
      id: 2,
      title: 'KPCA Bylaws',
      description: 'Detailed bylaws outlining membership requirements, procedures, and organizational structure',
      fileSize: '1.8 MB',
      lastUpdated: 'February 2024',
      category: 'Governance',
      downloadUrl: '/documents/kpca-bylaws.pdf',
      icon: '📖'
    },
    {
      id: 3,
      title: 'Current Procurement Act',
      description: 'Kenya Public Procurement and Asset Disposal Act - Current regulations affecting cleaning service providers',
      fileSize: '3.2 MB',
      lastUpdated: 'January 2024',
      category: 'Legal Framework',
      downloadUrl: '/documents/procurement-act-2024.pdf',
      icon: '⚖️'
    },
    {
      id: 4,
      title: 'KPCA Code of Conduct',
      description: 'Professional standards and ethical guidelines for KPCA members',
      fileSize: '950 KB',
      lastUpdated: 'March 2024',
      category: 'Standards',
      downloadUrl: '/documents/kpca-code-of-conduct.pdf',
      icon: '🤝'
    },
    {
      id: 5,
      title: 'Membership Application Form',
      description: 'Complete application form for new KPCA membership registration',
      fileSize: '650 KB',
      lastUpdated: 'April 2024',
      category: 'Membership',
      downloadUrl: '/documents/membership-application-form.pdf',
      icon: '📝'
    },
    {
      id: 6,
      title: 'Quality Standards Guidelines',
      description: 'KPCA approved quality standards and best practices for professional cleaning services',
      fileSize: '2.5 MB',
      lastUpdated: 'February 2024',
      category: 'Standards',
      downloadUrl: '/documents/quality-standards-guidelines.pdf',
      icon: '✨'
    }
  ];

  const categories = [...new Set(documents.map(doc => doc.category))];

  const handleDownload = (doc) => {
    // In a real implementation, this would trigger the actual download
    // For now, we'll show an alert
    alert(`Downloading: ${doc.title}\n\nNote: This is a demo. In production, this would download the actual PDF file.`);
  };

  return (
    <main className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-gray-900 text-white py-16">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              📥 Downloads & Resources
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Access important KPCA documents, regulations, and resources. All documents are available in PDF format for easy viewing and printing.
            </p>
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          {/* Category Filter */}
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Document Categories</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <span
                  key={category}
                  className="px-4 py-2 bg-primary-100 text-primary-800 rounded-full text-sm font-medium"
                >
                  {category}
                </span>
              ))}
            </div>
          </div>

          {/* Documents Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {documents.map((doc) => (
              <div key={doc.id} className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 p-6">
                <div className="text-center mb-4">
                  <div className="text-4xl mb-3">{doc.icon}</div>
                  <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium mb-3">
                    {doc.category}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                  {doc.title}
                </h3>
                
                <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                  {doc.description}
                </p>
                
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>File Size:</span>
                    <span className="font-medium">{doc.fileSize}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Last Updated:</span>
                    <span className="font-medium">{doc.lastUpdated}</span>
                  </div>
                </div>
                
                <button
                  onClick={() => handleDownload(doc)}
                  className="w-full mt-6 bg-primary-600 hover:bg-primary-700 text-white py-3 px-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="bg-blue-50 py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-blue-100 rounded-lg p-6">
              <h3 className="text-2xl font-bold text-blue-900 mb-4">
                📢 Important Notice
              </h3>
              <p className="text-blue-800 leading-relaxed">
                All documents provided are for informational purposes and reflect the most current versions available. 
                For legal or official purposes, please verify document versions with KPCA headquarters. 
                Members are encouraged to stay updated with any changes to regulations and guidelines.
              </p>
              <div className="mt-6">
                <a
                  href="/contact"
                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg font-medium transition-colors duration-200"
                >
                  Contact Us for More Information
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Related Resources</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <a
                href="/membership"
                className="block p-6 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200"
              >
                <h4 className="text-lg font-bold mb-2">👥 Join KPCA</h4>
                <p className="text-sm">Become a certified member today</p>
              </a>
              <a
                href="/about"
                className="block p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200"
              >
                <h4 className="text-lg font-bold mb-2">ℹ️ About KPCA</h4>
                <p className="text-sm">Learn about our mission and values</p>
              </a>
              <a
                href="/contact"
                className="block p-6 bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all duration-200"
              >
                <h4 className="text-lg font-bold mb-2">📞 Contact Us</h4>
                <p className="text-sm">Get in touch with our team</p>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Downloads; 